import { useContext, useState } from 'react';

import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { Box, FormLabel } from '@material-ui/core';
import AppContext from '../../../../AppContext';
import { Exam, User } from '../../../../types';
import ConfirmationDialog from '../../../../components/ConfirmationDialog';
import FormSection from '../../../../components/form/FormSection';
import TextOutput from '../../../../components/form/TextOutput';
import DateOutput from '../../../../components/form/DateOutput';
import ButtonRow from '../../../../components/ButtonRow';
import InlineButton from '../../../../components/InlineButton';
import InlineLink from '../../../../components/InlineLink';
import ExamRepository from '../../repository/ExamRepository';
import { EXAM_BASE_ROUTE, FUTURE_EXAM_OVERVIEW_ROUTE } from '../../index';
import ReadOnlyExamDiplomaList from '../../components/ReadOnlyExamDiplomaList';
import ExamLocationInfo from '../../components/ExamLocationInfo';
import FileChips from '../../../../components/file/FileChips';
import ExamSwimmingLessonProviderInfo from '../../components/ExamSwimmingLessonProviderInfo';
import CandidateExamRegisterDialog from '../../../delegates/components/CandidateExamRegisterDialog';
import UpdateAttendanceDialog from '../../../delegates/components/UpdateAttendanceDialog';
import { getExamPermissions } from '../../utils';

interface ExamFormSectionProps {
  exam: Exam;
}

const ExamSection = (props: ExamFormSectionProps) => {
  const { exam } = props;

  const { roleViewManager } = useContext(AppContext);

  const history = useHistory();
  const notifications = useSnackbar();
  const account = useSelector(
    (selector: {
      user: {
        account: User;
      };
    }) => selector.user.account,
  );

  const examPermissions = getExamPermissions(exam, account, roleViewManager);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState<boolean>(false);

  const [registrationDialogOpen, setRegistrationDialogOpen] =
    useState<boolean>(false);
  const [updateAttendanceDialogOpen, setUpdateAttendanceDialogOpen] =
    useState<boolean>(false);

  const openDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handlePDFDownload = () => {
    window.open(
      `${process.env.REACT_APP_API_URL}/api/v1/exams/${exam.id}/pdf`,
      '_blank',
      'noreferrer,noopener',
    );
  };

  const handleClose = (exam: Exam) => {
    new ExamRepository()
      .close(exam.id)
      .then(() => {
        notifications.enqueueSnackbar('Het examen is afgesloten.', {
          variant: 'success',
        });
        history.go(0);
      })
      .catch(() => {
        notifications.enqueueSnackbar(
          'Fout bij het afsluiten van het examen.',
          {
            variant: 'error',
          },
        );
      });
  };

  const handleDelete = (exam: Exam) => {
    new ExamRepository()
      .delete(exam.id)
      .then(() => {
        notifications.enqueueSnackbar('Het examen is geannuleerd.', {
          variant: 'success',
        });
      })
      .catch(() => {
        notifications.enqueueSnackbar(
          'Fout bij het annuleren van het examen.',
          {
            variant: 'error',
          },
        );
      })
      .finally(() => {
        history.push(FUTURE_EXAM_OVERVIEW_ROUTE);
      });
  };

  const examToLessonLocations = (exam: Exam) =>
    exam.lessonLocations
      .map((lessonLocation) => lessonLocation.name)
      .join(', ') || '';

  return (
    <>
      <FormSection title="Over het examen">
        <TextOutput label="Nummer" value={exam.examNumber} />
        <DateOutput label="Datum" value={exam.date} />
        <Box maxWidth={500}>
          <FormLabel>Zwemlesaanbieder</FormLabel>
          <ExamSwimmingLessonProviderInfo
            examSwimmingLessonProvider={exam.swimmingLessonProvider}
          />
        </Box>
        <Box maxWidth={500}>
          <FormLabel>Afzwembad</FormLabel>
          <ExamLocationInfo examLocation={exam.examLocation} />
        </Box>
        {!exam.swimmingLessonProvider.centralExams && (
          <TextOutput label="Leslocaties" value={examToLessonLocations(exam)} />
        )}
        {exam.examiner && (
          <TextOutput label="Examinator" value={exam.examiner.fullName} />
        )}
        <ReadOnlyExamDiplomaList
          oneExaminerOverall={exam.examiner !== undefined}
          examLines={exam.examLines}
        />
        {exam.customizedDiplomaTypePdfs &&
          exam.customizedDiplomaTypePdfs.length >= 1 && (
            <FileChips files={exam.customizedDiplomaTypePdfs} />
          )}
        {examPermissions.canViewDelegate && exam.delegate && (
          <TextOutput label="Gedelegeerde" value={exam.delegate.fullName} />
        )}
        {examPermissions.canViewDelegate && exam.aspirant && (
          <TextOutput label="Aspirant" value={exam.aspirant.fullName} />
        )}
        {exam.closedAt && (
          <DateOutput label="Afgesloten op" value={exam.closedAt} />
        )}
      </FormSection>

      <ButtonRow>
        {examPermissions.canEdit && (
          <InlineLink
            type="primary"
            to={`${EXAM_BASE_ROUTE}/${exam.id}/bewerken`}
          >
            Bewerken
          </InlineLink>
        )}
        {examPermissions.canRegister && (
          <InlineButton
            type="primary"
            onClick={() => setRegistrationDialogOpen(true)}
          >
            Aanmelden
          </InlineButton>
        )}
        {examPermissions.canUpdateAttendance && (
          <InlineButton
            type="primary"
            onClick={() => setUpdateAttendanceDialogOpen(true)}
          >
            Beschikbaarheid aanpassen
          </InlineButton>
        )}
        <InlineButton onClick={() => handlePDFDownload()}>
          PDF downloaden
        </InlineButton>
        {examPermissions.canClose && (
          <InlineButton
            type="dangerous"
            onClick={() => setCloseDialogOpen(true)}
          >
            Examen afsluiten
          </InlineButton>
        )}
        {examPermissions.canDelete && (
          <InlineButton type="dangerous" onClick={() => openDeleteDialog()}>
            Examen annuleren
          </InlineButton>
        )}
      </ButtonRow>
      {examPermissions.canRegister && (
        <CandidateExamRegisterDialog
          exam={props.exam}
          open={registrationDialogOpen}
          onClose={() => setRegistrationDialogOpen(false)}
        />
      )}
      {examPermissions.canUpdateAttendance && (
        <UpdateAttendanceDialog
          exam={props.exam}
          open={updateAttendanceDialogOpen}
          onClose={() => setUpdateAttendanceDialogOpen(false)}
        />
      )}
      {examPermissions.canDelete && (
        <ConfirmationDialog
          title="Examen annuleren"
          query="Weet je zeker dat je dit examen wilt annuleren?"
          buttonText="Bevestigen"
          isOpen={deleteDialogOpen}
          onClose={closeDeleteDialog}
          onConfirm={() => handleDelete(exam)}
        />
      )}
      {examPermissions.canClose && (
        <ConfirmationDialog
          title="Examen afsluiten"
          query="Weet je zeker dat je dit examen wilt afsluiten?"
          buttonText="Bevestigen"
          isOpen={closeDialogOpen}
          onClose={() => setCloseDialogOpen(false)}
          onConfirm={() => handleClose(exam)}
        />
      )}
    </>
  );
};

export default ExamSection;
